package kz.dentalcrm.app.screens.reply_by_template

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import androidx.compose.material3.FloatingActionButton
import compose.icons.feathericons.Plus
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.answer_to_whats_app
import dentalcrm.composeapp.generated.resources.reply_by_template
import dentalcrm.composeapp.generated.resources.reply_message_hint
import dentalcrm.composeapp.generated.resources.send_telegram
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UIEmptyListState
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import kz.dentalcrm.app.core.ui.components.UISelfTextInput
import kz.dentalcrm.app.core.ui.components.refresh.PullToRefresh
import kz.dentalcrm.app.core.ui.components.refresh.rememberPullToRefreshState
import kz.dentalcrm.app.screens.quick_replies.add.AddQuickReplyDialog
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.screens.protocols.detail.DetailProtocolEvent
import kz.dentalcrm.app.screens.reply_by_template.mvi.ReplyByTemplateViewEvent
import kz.dentalcrm.app.screens.reply_by_template.mvi.ReplyByTemplateViewSideEffect
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.theme.DefaultTheme
import kz.dentalcrm.app.theme.shimmer.ShimmerBounds
import kz.dentalcrm.app.theme.shimmer.rememberShimmer
import kz.dentalcrm.app.theme.shimmer.shimmer
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ReplyByTemplateScreen(phone: String) {
    val navigator: AppNavigator = koinInject()
    val viewModel: ReplyByTemplateViewModel = koinViewModel()
    val state by viewModel.viewState.collectAsState()
    val event: (ReplyByTemplateViewEvent) -> Unit = viewModel::setEvent
    val uriHandler = LocalUriHandler.current

    LaunchedEffect(Unit) {
        event(ReplyByTemplateViewEvent.Init(phone))
    }

    LaunchedEffect(viewModel.effect) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is ReplyByTemplateViewSideEffect.OpenUrl -> {
                    runCatching { uriHandler.openUri(effect.url) }
                }
                ReplyByTemplateViewSideEffect.NavigateBack -> navigator.navigateUp()
            }
        }
    }

    UiStateScaffold(
        modifier = Modifier.fillMaxSize()
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .imePadding()
        ) {
            ContentHeader(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 12.dp),
                text = stringResource(Res.string.reply_by_template),
                leadingContent = {
                    UIIconBackButton(
                        onClick = {
                            navigator.navigateUp()
                        }
                    )
                }
            )

            val shimmer = rememberShimmer(ShimmerBounds.Window)
            val isLoading = state.isLoading
            var isRefreshing by remember { mutableStateOf(false) }
            val pullToRefreshState = rememberPullToRefreshState(isRefreshing = isRefreshing)
            LaunchedEffect(isLoading) { if (!isLoading) isRefreshing = false }

            var showAddDialog by remember { mutableStateOf(false) }

            Box(modifier = Modifier.weight(1f)) {
                PullToRefresh(
                    state = pullToRefreshState,
                    onRefresh = { isRefreshing = true; event(ReplyByTemplateViewEvent.Reload) },
                    modifier = Modifier.fillMaxSize()
                ) {
                    when {
                        isLoading && state.quickReplies.isEmpty() -> {
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(horizontal = 16.dp)
                            ) {
                                Spacer(modifier = Modifier.height(16.dp))
                                repeat(8) { ReplySkeletonItem(shimmer) }
                            }
                        }
                        !isLoading && state.quickReplies.isEmpty() -> {
                            UIEmptyListState()
                        }
                        else -> {
                            val listModifier = if (isLoading)
                                Modifier.fillMaxSize().padding(horizontal = 16.dp).shimmer(shimmer)
                            else
                                Modifier.fillMaxSize().padding(horizontal = 16.dp)
                            LazyColumn(modifier = listModifier) {
                                item { Spacer(modifier = Modifier.height(16.dp)) }
                                items(state.quickReplies, key = { it.id }) { reply ->
                                    ContentButton(
                                        modifier = Modifier.fillMaxWidth(),
                                        shape = RoundedCornerShape(16.dp),
                                        onClick = { event(ReplyByTemplateViewEvent.SelectReply(reply)) }
                                    ) {
                                        Row(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .background(
                                                    color = DefaultTheme.colors.primarySubdued,
                                                    shape = RoundedCornerShape(16.dp)
                                                )
                                                .padding(horizontal = 16.dp, vertical = 12.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Text(
                                                text = reply.message ?: "",
                                                style = DefaultTheme.typography.bodyMediumMedium,
                                                color = DefaultTheme.colors.onSurface,
                                                maxLines = 2,
                                                overflow = TextOverflow.Ellipsis
                                            )
                                        }
                                    }
                                    Spacer(modifier = Modifier.height(12.dp))
                                }
                                item { Spacer(modifier = Modifier.height(80.dp)) }
                            }
                        }
                    }
                }
                FloatingActionButton(
                    modifier = Modifier
                        .padding(bottom = 16.dp, end = 16.dp)
                        .align(Alignment.BottomEnd),
                    shape = RoundedCornerShape(50),
                    containerColor = DefaultTheme.colors.primary,
                    contentColor = DefaultTheme.colors.onPrimary,
                    onClick = { showAddDialog = true }
                ) {
                    Icon(imageVector = FeatherIcons.Plus, contentDescription = null)
                }
            }

            if (showAddDialog) {
                AddQuickReplyDialog(
                    onAdd = { event(ReplyByTemplateViewEvent.Reload) },
                    onDismiss = { showAddDialog = false }
                )
            }

            HorizontalDivider(
                color = DefaultTheme.colors.onSurfaceVariant.copy(alpha = 0.15f)
            )

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(DefaultTheme.colors.surface)
                    .padding(horizontal = 16.dp, vertical = 12.dp)
                    .navigationBarsPadding()
            ) {
                UISelfTextInput(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 120.dp),
                    maxLines = 10,
                    singleLine = false,
                    label = stringResource(Res.string.reply_message_hint),
                    value = state.replyMessageInput,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Text
                    ),
                    valueChanged = { event(ReplyByTemplateViewEvent.MessageChanged(it)) }
                )
                Spacer(modifier = Modifier.height(12.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    UIButton(
                        modifier = Modifier.weight(1f),
                        text = stringResource(Res.string.send_telegram),
                        onClick = { event(ReplyByTemplateViewEvent.SendTelegram) }
                    )
                    UIButton(
                        modifier = Modifier.weight(1f),
                        text = stringResource(Res.string.answer_to_whats_app),
                        onClick = { event(ReplyByTemplateViewEvent.SendWhatsApp) }
                    )
                }
            }
        }
    }
}

@Composable
private fun ReplySkeletonItem(shimmer: kz.dentalcrm.app.theme.shimmer.Shimmer) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = DefaultTheme.colors.primarySubdued,
                shape = RoundedCornerShape(16.dp)
            )
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.85f)
                    .height(14.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(6.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.5f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.08f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
        }
    }
    Spacer(modifier = Modifier.height(12.dp))
}
